import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { msg } from "@lingui/macro";
import { ContractsService } from "@/api";
import type {
  documents_ListSubsidyDocumentsResponse,
  documents_SubsidyDocumentResponse,
  documents_UploadSubsidyDocumentRequest,
} from "@/api";

export type SubsidyDocument = documents_SubsidyDocumentResponse;

// Subsidy documents of a contract version (issue #2002): BF and SBU
// confirmations, the signed Finanzierungsantrag, the Elternvereinbarung, the
// city's decision and the KmbB papers. The list holds the version's documents
// and those of its earlier versions, so every write invalidates every version.
export const subsidyDocumentKeys = {
  all: ["subsidy-documents"] as const,
  byContract: (orgId: string | null, contractId: string) =>
    [...subsidyDocumentKeys.all, orgId, contractId] as const,
};

export function useSubsidyDocuments(contractId: string | null | undefined) {
  const { orgId } = useAuth();
  return useQuery<documents_ListSubsidyDocumentsResponse>({
    queryKey: contractId
      ? subsidyDocumentKeys.byContract(orgId ?? null, contractId)
      : [...subsidyDocumentKeys.all, "none"],
    queryFn: () => ContractsService.getApiV1ContractsSubsidyDocuments(contractId as string),
    enabled: !!contractId && !!orgId,
  });
}

export function useUploadSubsidyDocument(contractId: string) {
  const queryClient = useQueryClient();
  return useMutation<SubsidyDocument, unknown, documents_UploadSubsidyDocumentRequest>({
    mutationFn: (request) => ContractsService.postApiV1ContractsSubsidyDocuments(contractId, request),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: subsidyDocumentKeys.all });
    },
    meta: {
      successMessage: msg({ id: "subsidyDocuments.upload.success", message: "Document saved" }),
      errorFallback: msg({ id: "subsidyDocuments.upload.error", message: "Could not save the document. Please try again." }),
    },
  });
}

export function useDeleteSubsidyDocument(contractId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (documentId) => ContractsService.deleteApiV1ContractsSubsidyDocuments(contractId, documentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: subsidyDocumentKeys.all });
    },
    meta: {
      successMessage: msg({ id: "subsidyDocuments.delete.success", message: "Document deleted" }),
      errorFallback: msg({ id: "subsidyDocuments.delete.error", message: "Could not delete the document. Please try again." }),
    },
  });
}

/**
 * The AUDITED download: a fresh presigned url plus a `download` row in the
 * document access log. A programmatic anchor click, not window.open: the url
 * arrives after the request, outside the click's user gesture (#1297).
 */
export function useDownloadSubsidyDocument(contractId: string) {
  return useMutation({
    mutationFn: (documentId: string) =>
      ContractsService.getApiV1ContractsSubsidyDocumentsDownload(contractId, documentId),
    onSuccess: (data) => {
      if (!data?.download_url) return;
      const anchor = document.createElement("a");
      anchor.href = data.download_url;
      anchor.rel = "noopener noreferrer";
      anchor.download = data.filename ?? "";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    },
    meta: {
      errorFallback: msg({ id: "documents.download.error", message: "Download failed" }),
    },
  });
}
